'use client'

import Link from 'next/link'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { LabMachine } from './lab-machine'
import { Gauge, Led, Lever, RotaryKnob, Screw } from './lab-controls'
import { creamPress } from './lab-styles'

export interface LabGpu {
  id: string
  instanceType: string
  name: string
  equivalent: string
  vram: number
  vcpus: number
  ram: number
  /** Formatted base rates from the billing rate table. */
  onDemand: string
  spot: string
  /** True for the lowest on-demand base rate on sale. */
  isStartingRate: boolean
  /** One configuration priced in full by the billing function. */
  example: { region: string; multiplier: string; upload: string; onDemand: string; spot: string }
  launchHref: string
}

interface LabHeroProps {
  /** Rentable GPUs, cheapest on-demand rate first. */
  gpus: LabGpu[]
  /** The "Explore GPUs" button, rendered on the server. */
  exploreAction: ReactNode
  regionCount: number
  includedDiskGb: number
  billingNote: string
}

type Mode = 'on-demand' | 'spot'

export function LabHero({ gpus, exploreAction, regionCount, includedDiskGb, billingNote }: LabHeroProps) {
  const [index, setIndex] = useState(0)
  const [mode, setMode] = useState<Mode>('on-demand')
  const [power, setPower] = useState(true)
  const [fan, setFan] = useState(55)
  const [pulse, setPulse] = useState(0)
  const copyRef = useRef<HTMLDivElement>(null)
  const machineRef = useRef<HTMLDivElement>(null)

  const gpu = gpus[index] ?? gpus[0]
  const maxVram = Math.max(...gpus.map((g) => g.vram))
  const maxCpu = Math.max(...gpus.map((g) => g.vcpus))
  const maxRam = Math.max(...gpus.map((g) => g.ram))
  const bump = () => setPulse((p) => p + 1)

  // Light parallax: the machine trails the scroll a little, the copy less.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    let raf = 0
    const apply = () => {
      raf = 0
      const y = Math.min(window.scrollY, 900)
      if (machineRef.current) machineRef.current.style.transform = `translate3d(0, ${(y * 0.14).toFixed(1)}px, 0)`
      if (copyRef.current) copyRef.current.style.transform = `translate3d(0, ${(y * 0.05).toFixed(1)}px, 0)`
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(raf)
    }
  }, [])

  if (!gpu) return null

  const price = mode === 'spot' ? gpu.spot : gpu.onDemand
  const examplePrice = mode === 'spot' ? gpu.example.spot : gpu.example.onDemand
  const isStartingRate = gpu.isStartingRate && mode === 'on-demand'
  const facts = [
    { value: String(regionCount), label: 'regions' },
    { value: String(gpus.length), label: 'GPU configurations' },
    { value: `${includedDiskGb} GB`, label: 'disk included' },
  ]

  return (
    <section aria-labelledby="hero-title" className="lab-room relative isolate overflow-hidden">
      <div aria-hidden className="lab-texture pointer-events-none absolute inset-0 -z-10" />
      <div
        aria-hidden
        className="lab-lightpool pointer-events-none absolute -right-[15%] top-0 -z-10 h-[80%] w-[85%] lg:w-[70%]"
      />
      <div aria-hidden className="lab-bench pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[42%]" />

      <div className="mx-auto w-full max-w-[1320px] px-5 pb-10 pt-10 md:px-8 lg:pb-14 lg:pt-12">
        <div className="grid items-center gap-2 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div ref={copyRef} className="relative z-10 max-w-xl">
            <p className="lab-rise lab-chip inline-flex items-center gap-2.5 px-3 py-1.5 text-sm font-bold uppercase tracking-[0.12em] text-background">
              <Led on className="lab-blink" />
              The compute lab
            </p>
            <h1
              id="hero-title"
              className="lab-rise mt-6 font-heading text-[clamp(3rem,7.2vw,6.75rem)] font-bold uppercase leading-[0.86] tracking-[-0.045em] text-background"
              style={{ animationDelay: '80ms' }}
            >
              Built for the <span className="lab-glow-text text-accent">curious.</span>
            </h1>
            <p
              className="lab-rise mt-6 max-w-md text-base leading-relaxed text-background/75"
              style={{ animationDelay: '160ms' }}
            >
              Rent a Windows cloud PC with an NVIDIA GPU for gaming, rendering, AI or compute. Pick a GPU and a region,
              start it when you need it and connect over Remote Desktop. Usage is metered by the minute and paid from
              your wallet.
            </p>
            <div className="lab-rise mt-8 flex flex-col gap-4 sm:flex-row" style={{ animationDelay: '240ms' }}>
              {exploreAction}
              <Button className={creamPress} asChild>
                <Link href={gpu.launchHref}>Launch Your Machine</Link>
              </Button>
            </div>
            <ul className="lab-rise mt-8 flex flex-wrap gap-2 text-sm" style={{ animationDelay: '320ms' }}>
              {facts.map((fact) => (
                <li key={fact.label} className="lab-chip px-3 py-2">
                  <span className="font-bold text-background">{fact.value}</span>{' '}
                  <span className="text-background/65">{fact.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div ref={machineRef} className="relative -mx-5 h-[400px] sm:h-[480px] md:mx-0 lg:-mr-12 lg:h-[660px]">
            <LabMachine className="h-full w-full" power={power} fanSpeed={fan / 100} load={gpu.vram / maxVram} pulse={pulse} />
            <p className="pointer-events-none absolute right-4 top-3 hidden text-sm text-background/50 lg:block">
              Drag to turn it, click to pulse it
            </p>
          </div>
        </div>

        {/* Bench console */}
        <div className="lab-deck-in lab-plate relative z-10 mt-2 px-4 pb-4 pt-3 sm:px-6 sm:pb-5 lg:-mt-20">
          <Screw className="left-2 top-2" angle={20} />
          <Screw className="right-2 top-2" angle={110} />
          <Screw className="bottom-2 left-2" angle={70} />
          <Screw className="bottom-2 right-2" angle={150} />

          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-2 pb-3">
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-background">
              <Led on={power} className={power ? 'lab-blink' : undefined} />
              Bench console
            </p>
            <p className="text-xs text-background/60">Demo controls. They drive the model above, not a real machine.</p>
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.05fr)]">
            {/* Selector + illuminated rate display */}
            <div className="lab-well flex flex-col items-stretch gap-4 p-3 sm:flex-row sm:items-center sm:p-4">
              <RotaryKnob
                label="GPU"
                value={index}
                min={0}
                max={gpus.length - 1}
                wrap
                valueText={gpu.name.replace(/^NVIDIA\s+/, '')}
                onChange={(v) => {
                  setIndex(v)
                  bump()
                }}
              />
              <div className="lab-lcd min-w-0 flex-1 px-4 py-3" aria-live="polite">
                <div className="flex items-center justify-between gap-3 text-xs font-bold uppercase tracking-[0.1em]">
                  <span className="truncate">{gpu.name}</span>
                  <span className="shrink-0 opacity-80">{power ? (mode === 'spot' ? 'Spot' : 'On-demand') : 'Standby'}</span>
                </div>
                <p
                  key={`${gpu.id}-${mode}-${power}`}
                  className={cn(
                    'lab-flicker lab-glow-text mt-2 text-4xl font-bold leading-none tabular-nums sm:text-[2.5rem]',
                    !power && 'opacity-40'
                  )}
                >
                  {isStartingRate && <span className="mr-2 text-base font-bold uppercase">From</span>}
                  {price}
                  <span className="ml-1 text-base">/hr</span>
                </p>
                <p className="mt-2 text-xs leading-snug opacity-80">
                  {isStartingRate ? 'Starting rate' : 'Base rate'}, before region and upload tier.
                </p>
                <p className="text-xs leading-snug opacity-65">
                  {gpu.example.region} {gpu.example.multiplier}, {gpu.example.upload}, {includedDiskGb} GB disk:{' '}
                  <span className="font-bold opacity-100">{examplePrice}/hr</span>
                </p>
              </div>
            </div>

            {/* Mechanical controls */}
            <div className="lab-well flex flex-wrap items-start justify-around gap-3 p-3 sm:p-4">
              <RotaryKnob
                label="Fan"
                value={fan}
                min={0}
                max={100}
                step={5}
                valueText={`${fan}%`}
                onChange={(v) => {
                  setFan(v)
                  if (v % 25 === 0) bump()
                }}
              />
              <Lever
                label="Power"
                on={power}
                offLabel="Off"
                onLabel="On"
                onToggle={() => {
                  setPower((p) => !p)
                  bump()
                }}
              />
              <Lever
                label="Billing"
                on={mode === 'spot'}
                offLabel="On-demand"
                onLabel="Spot"
                onToggle={() => {
                  setMode((m) => (m === 'spot' ? 'on-demand' : 'spot'))
                  bump()
                }}
              />
            </div>

            {/* Performance gauges for the selected GPU */}
            <div className="lab-well grid grid-cols-3 gap-2 p-3 sm:p-4">
              <Gauge label="VRAM" value={gpu.vram} max={maxVram} unit="GB" on={power} />
              <Gauge label="vCPU" value={gpu.vcpus} max={maxCpu} on={power} />
              <Gauge label="RAM" value={gpu.ram} max={maxRam} unit="GB" on={power} />
            </div>
          </div>

          <p className="mt-3 px-2 text-xs leading-relaxed text-background/55">{billingNote}</p>
        </div>
      </div>
    </section>
  )
}
